// R4.R2 — LegacyCampaignRedirect
// Resolves a legacy /admin/campaigns/:legacyId URL by looking up the channel's
// parent group id, then replaces the URL with the canonical form.
import { Navigate, useParams } from 'react-router-dom';
import { useChannelById } from '../hooks/useChannelDetail';
import { LoadingSkeleton } from '@/components/feedback/LoadingSkeleton';

interface Props {
  suffix?: string;
}

// Inner component — called only when id is not "new" (React hooks rule: no
// conditional hook calls; the outer shell handles the "new" short-circuit).
function ChannelResolver({ id, suffix }: { id: string; suffix: string }) {
  const { data, isLoading, isError } = useChannelById(id);

  if (isLoading) {
    return (
      <div role="status" aria-label="Redirecting…" className="p-6">
        <LoadingSkeleton variant="detail" />
      </div>
    );
  }

  if (isError || !data || !data.campaignGroupId) {
    // Not a known channel — treat id as a group id; let CampaignGroupDetailPage
    // show the 404 if it doesn't exist either.
    return <Navigate to={`/admin/campaigns/${id}`} replace />;
  }

  return (
    <Navigate
      to={`/admin/campaigns/${data.campaignGroupId}/channels/${data.id}${suffix}`}
      replace
    />
  );
}

export function LegacyCampaignRedirect({ suffix = '' }: Props) {
  const { legacyId } = useParams<{ legacyId: string }>();
  const id = legacyId ?? '';

  // Guard: /admin/campaigns/new must not be consumed as a legacy channel id.
  if (id === 'new') {
    return <Navigate to="/admin/campaigns" replace />;
  }

  return <ChannelResolver id={id} suffix={suffix} />;
}

export default LegacyCampaignRedirect;
